import type { IDocumentStatus } from '@/types/document'
import type { ITextDocumentVersionStatus } from '@/types/textDocument'
import { UNCLASSIFIED, type IDisplayClassification } from '@/lib/constants/documents'

export const DOCUMENT_STATUS: { [K in IDocumentStatus]: string } = {
  notApplicable: 'No Aplicable',
  notStarted: 'No Iniciado',
  inProgress: 'En Curso',
  completed: 'Completado',
  approved: 'Aprobado',
}

export const TEXT_DOCUMENT_VERSION_STATUS: { [K in ITextDocumentVersionStatus]: string } = {
  draft: 'Borrador',
  completed: 'Completado',
  approved: 'Aprobado',
}

export const DOCUMENT_CLASSIFICATION: { [K in IDisplayClassification]: string } = {
  Public: 'Público',
  Internal: 'Interno',
  Confidential: 'Confidencial',
  'Commercial in Confidence': 'Comercial confidencial',
  [UNCLASSIFIED]: 'Sin clasificar',
}

export const DOCUMENT_TABLE = {
  TITLE: 'Documentos',
  NAME: 'Nombre',
  STATUS: 'Estado',
  VERSION: 'Versión',
  OWNER: 'Responsable',
  LAST_UPDATED: 'Última Edición',
  CLUSTER: 'Cluster',
  ID: 'ID',
  TREATMENT: 'Tratamiento',
  SEARCH_PLACEHOLDER: 'Buscar documentos...',
  NEW_DOCUMENT: 'Nuevo Documento',
  BACK_TO_LIST: 'Volver',
  UNTITLED: 'Documento sin título',
  ORGANIZATION_LOGO: 'Logo de la organización',
  EXPORT_PDF: 'Exportar PDF',
}

export const TEXT_DOCUMENT_VERSIONS = {
  CREATE_NEW_VERSION: 'Crear nueva versión',
  EDIT_DRAFT: (year: number) => `Editar borrador ${year}`,
  TITLE_WITH_COUNT: (count: number) => `Documento de Qalea (${count} ${count === 1 ? 'versión' : 'versiones'})`,
  VERSION_NUMBER: (versionNumber: number) => `v${versionNumber}`,
  EMPTY: 'Todavía no hay versiones',
  REVIEW: 'Revisar',
  SUPERSEDED: 'Aprobada anterior',
  CREATED_BY: (name: string) => `Creada por ${name}`,
  APPROVED_BY: (name: string) => `Aprobada por ${name}`,
  UNKNOWN_USER: 'Usuario desconocido',
  APPROVE: 'Aprobar versión',
  APPROVE_TITLE: '¿Aprobar esta versión?',
  APPROVE_DESCRIPTION:
    'Una vez aprobada, esta versión pasa a ser de solo lectura. Cualquier cambio requiere una nueva versión.',
  CHANGE_SUMMARY: 'Resumen de cambios',
  CHANGE_SUMMARY_FIELD: 'Resumen de cambios (opcional)',
  CHANGE_SUMMARY_PLACEHOLDER: '¿Qué ha cambiado en esta versión y por qué?',
  SHARED_LINK_NOT_UPDATED:
    'Guardado, pero no se pudo actualizar el vínculo del documento del marco. Inténtalo de nuevo.',
  MARK_COMPLETED: 'Marcar como completado',
  REOPEN: 'Reabrir como borrador',
}

export const TEXT_DOCUMENT_EDITOR = {
  UNAVAILABLE_TITLE: 'No se ha podido cargar el documento',
  UNAVAILABLE_BODY:
    'No hemos podido conectar con el servicio de edición. Recarga la página dentro de unos instantes para volver a intentarlo.',
}

// Emailed to someone mentioned with `@` in a text document or in one of its comments.
export const TEXT_DOCUMENT_MENTION = {
  EMAIL_SUBJECT: (authorName: string, documentName: string) => `${authorName} te ha mencionado en ${documentName}`,
  EMAIL_TEXT: (authorName: string, documentName: string, url: string) =>
    `${authorName} te ha mencionado en ${documentName}. Abre el documento: ${url}`,
  EMAIL_HTML: (authorName: string, documentName: string, url: string) => `
  <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <p>${authorName} te ha mencionado en <strong>${documentName}</strong>.</p>
    <p><a href="${url}" style="color: #007bff; text-decoration: none;">Abrir el documento</a></p>
    <p>Gracias,<br>Equipo Qalea</p>
  </div>
`,
}

export const TEXT_DOCUMENT_ATTRIBUTES = {
  LINKED_DOCUMENT: 'Documento de cumplimiento',
  LINKED_TEXT_DOCUMENT: 'Documento de texto',
  LINKED_TO: (documentName: string) => `vinculado a ${documentName}`,
  CLAIMED_ELSEWHERE: (documentName: string) =>
    `Ese documento de texto ya está vinculado a ${documentName}. Desvincúlalo allí primero.`,
  UNLINKED: 'No vinculado',
  REMOVE: 'Quitar vínculo',
}

export const DOCUMENT_COLUMN_FILTER = {
  ALL: 'Todos',
  CLEAR: 'Limpiar',
  SEARCH_PLACEHOLDER: 'Buscar...',
  NO_RESULTS: 'No se encontraron resultados.',
}
export const DOCUMENT_BULK_ACTIONS = {
  SELECTED: 'seleccionados',
  DOCUMENT: 'documento',
  DOCUMENTS: 'documentos',
  UPDATING: 'Actualizando...',
  CLEAR: 'Limpiar',
}
export const DOCUMENT_OWNER_SELECTOR = {
  SELECT_OWNER: 'Seleccionar responsable',
  SEARCH_USERS: 'Buscar usuarios...',
  LOADING: 'Cargando...',
  NO_USER_FOUND: 'No se encontró ningún usuario.',
}
export const DOCUMENT_STATUS_SELECTOR = {
  SELECT_STATUS: 'Seleccionar estado',
}
export const DOCUMENT_FILTERS = {
  ALL: 'Todos',
  FILTER_BY_OWNER: 'Filtrar por responsable',
  FILTER_BY_CLUSTER: 'Filtrar por cluster',
  FILTER_BY_STANDARD: 'Filtrar por estándar',
}
export const DOCUMENT_DETAIL = {
  ATTRIBUTES: 'Atributos',
  CONTENT: 'Contenido del Documento',
  DESCRIPTION: 'Descripción del Documento',
  EDIT: 'Editar Documento',
  MISSING_DESCRIPTION: 'Falta la descripción del documento',
  RELATED_CONTROLS: 'Controles Relacionados',
  TITLE: 'Documento',
  CUSTOM_FILE_UPLOAD: 'Subir Archivo Personalizado',
  ADD_CUSTOM_LINK: 'Agregar Enlace Personalizado',
  NO_LINKS: 'Aún no se han agregado enlaces',
  NO_FILES: 'Aún no se han subido archivos',
  NO_SHARED_FILE: 'Aún no se ha subido un archivo compartido',
  DOWNLOAD_TEMPLATE: 'Ver Archivo Compartido',
  DOWNLOAD_CONTENT: 'Última Versión',
  MISSING_CONTENT: '',
  NEVER_UPDATED: 'Nunca actualizado',
  DOCUMENT_UPDATED: 'Documento actualizado',
  CUSTOM_LINKS: 'Enlaces Personalizados',
  CUSTOM_FILES: 'Archivos Personalizados',
  SHARED_FILE: 'Archivo Compartido',
  CUSTOM_FILE_DESCRIPTION: 'Puede subir su propio documento haciendo clic en el botón de abajo.',
  UPLOADED_AT: 'Subido el',
  FILE_NOT_FOUND: 'Archivo no encontrado',
  LINK_LABEL: 'Etiqueta',
  LINK_LABEL_PLACEHOLDER: 'ej., Documentación',
  LINK_URL: 'URL',
  LINK_URL_PLACEHOLDER: 'https://ejemplo.com',
  CANCEL: 'Cancelar',
  ADD_LINK: 'Agregar Enlace',
  NO_EXTERNAL_LINKS: 'No se han agregado enlaces externos',
}

export const DOCUMENT_COMMENTS = {
  SHOW_COMMENTS: 'Mostrar comentarios',
  HIDE_COMMENTS: 'Ocultar comentarios',
  ADD_COMMENT: 'Añadir comentario',
  NEW_COMMENT_PLACEHOLDER: 'Escribe un comentario...',
  EDIT_COMMENT_PLACEHOLDER: 'Editar comentario...',
  REPLY_PLACEHOLDER: 'Añadir comentario...',
  SAVE: 'Guardar',
  CANCEL: 'Cancelar',
  EDITED: 'editado',
  ORIGINAL_CONTENT_DELETED: 'Contenido original eliminado',
  RESOLVE: 'Resolver',
  REOPEN: 'Reabrir',
  EDIT_COMMENT: 'Editar comentario',
  DELETE_COMMENT: 'Eliminar comentario',
  MORE_ACTIONS: 'Más acciones',
}
